import { useId, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/cn";
import { useFormSkin } from "./FormSkinContext";
import { CheckIcon } from "./icons";
import { useRequiredIndicator } from "./RequiredIndicatorContext";

/** Checkbox classes per form skin (see FormSkinContext). */
const CHECKBOX_SKIN = {
  styleguide: {
    row: "text-midnight/70 flex cursor-pointer items-start gap-3 text-sm font-medium leading-relaxed",
    boxWrap: "relative mt-0.5 inline-flex shrink-0",
    box: "size-5 rounded",
    checkSize: 11,
    asterisk: "text-danger ml-0.5",
    errorIndent: "ml-8",
  },
  enroll: {
    row: "text-slate-teal flex cursor-pointer items-start gap-3 text-[15px] font-medium leading-relaxed",
    boxWrap: "relative inline-flex shrink-0",
    box: "size-[26px] rounded-[5px]",
    checkSize: 14,
    asterisk: "text-primary-text ml-0.5",
    errorIndent: "ml-[38px]",
  },
} as const;

interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Label is a ReactNode so callers can embed <strong>, links, etc. (useful for consent). */
  label: ReactNode;
  error?: string | undefined;
  /** Render an asterisk after the label. It does not set the native `required` attribute. */
  required?: boolean | undefined;
  ref?: Ref<HTMLInputElement> | undefined;
}

/**
 * Checkbox with an inline label.
 *
 * Pair it with react-hook-form via `<Checkbox {...register("foo")} />`.
 */
export function Checkbox({
  ref,
  label,
  error,
  required,
  className,
  id,
  ...rest
}: CheckboxProps): ReactNode {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = error ? `${inputId}-error` : undefined;
  // Opt-in "(required)" wording (see RequiredIndicatorContext); null = asterisk only.
  const requiredWord = useRequiredIndicator();
  const skin = CHECKBOX_SKIN[useFormSkin()];
  return (
    <div>
      <label htmlFor={inputId} className={skin.row}>
        {/* The native input keeps checkbox semantics and keyboard handling.
            `appearance-none` drops its default look, and the `peer-checked`
            icon overlay draws the check. */}
        <span className={skin.boxWrap}>
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            aria-invalid={error ? true : undefined}
            aria-describedby={errorId}
            {...rest}
            className={cn(
              "border-cloud-blue peer cursor-pointer appearance-none border-2 bg-transparent transition-colors",
              skin.box,
              "checked:border-ember checked:bg-ember",
              error && "border-danger",
              className,
            )}
          />
          <CheckIcon
            size={skin.checkSize}
            className="pointer-events-none invisible absolute inset-0 m-auto text-white peer-checked:visible"
          />
        </span>
        <span>
          {label}
          {required && (
            <span aria-hidden="true" className={skin.asterisk}>
              *
            </span>
          )}
          {required && requiredWord && (
            <span className="text-slate-teal ml-1">({requiredWord})</span>
          )}
        </span>
      </label>
      {error && (
        <p
          id={errorId}
          role="alert"
          className={cn("text-danger mt-1 text-[13px] leading-[1.5]", skin.errorIndent)}
        >
          {error}
        </p>
      )}
    </div>
  );
}
